import {
    EventName,
    HalfEvent,
    IEventMap,
    MatchPhone,
    PeriodNames,
    Timings,
    UserActionsCheckList,
} from "refsix-js-models";
import { recalculateStats } from "./matchService";
import { UserProfile } from "../models/userProfile";
import { copyObject } from "refsix-core";
import { updateUserActionsCheckList } from "./userActionsCheckListService";

/**
 * Check whether a match needs its playing periods to be set up.
 * Returns true if the match has fitness data but no (or only a single) half event.
 */
export function matchNeedsTimingsSetup(match: MatchPhone): boolean {
    if (!(match.hasTracking || match.hasHeartRate)) return false;
    if (match.timingsSkipped) return false;
    if (!match.matchEvents) return true;

    const halfEvents = Object.values(match.matchEvents).filter(
        (e) => e.eventName === EventName.half
    );
    // Needs setup if only 0-1 half events (single period or none)
    return halfEvents.length <= 1;
}

/**
 * Whether the one-time "how to set your playing periods" tutorial should be
 * played automatically. Only for users who have never saved playing periods
 * before - afterwards it is available on demand via the (i) icon.
 */
export function shouldShowPlayingPeriodsTutorial(
    match: MatchPhone,
    userActionsCheckList: UserActionsCheckList
): boolean {
    return (
        matchNeedsTimingsSetup(match) &&
        !userActionsCheckList?.hasCalibratedPlayingPeriods
    );
}

/**
 * Create 5 HalfEvent objects from 4 slider positions.
 *
 * | Index | Name        | playing | Start           | End             |
 * |-------|-------------|---------|-----------------|-----------------|
 * | 0     | Warm Up     | false   | activity start  | slider[0]       |
 * | 1     | First Half  | true    | slider[0]       | slider[1]       |
 * | 2     | Half Time   | false   | slider[1]       | slider[2]       |
 * | 3     | Second Half | true    | slider[2]       | slider[3]       |
 * | 4     | Warm Down   | false   | slider[3]       | activity end    |
 */
export function createHalfEventsFromSliders(
    positions: number[],
    startTime: number,
    endTime: number
): HalfEvent[] {
    const totalDuration = endTime - startTime;

    const sliderTimes = positions.map(
        (pos) => startTime + pos * totalDuration
    );

    const periods: {
        name: string;
        playing: boolean;
        start: number;
        end: number;
    }[] = [
        {
            name: PeriodNames.warmUp,
            playing: false,
            start: startTime,
            end: sliderTimes[0],
        },
        {
            name: PeriodNames.firstHalf,
            playing: true,
            start: sliderTimes[0],
            end: sliderTimes[1],
        },
        {
            name: PeriodNames.halfTime,
            playing: false,
            start: sliderTimes[1],
            end: sliderTimes[2],
        },
        {
            name: PeriodNames.secondHalf,
            playing: true,
            start: sliderTimes[2],
            end: sliderTimes[3],
        },
        {
            name: PeriodNames.warmDown,
            playing: false,
            start: sliderTimes[3],
            end: endTime,
        },
    ];

    return periods.map((p, index) =>
        new HalfEvent(
            Math.round(p.start),
            EventName.half,
            index,               // half
            0,                   // minuteOfPlay
            index,               // index
            p.name,
            p.playing,
            Math.round(p.end - p.start), // length in millis
            undefined,           // kickOffSide
            0,                   // injuryTimeAccumulated
            0,                   // injuryTimeInInjuryTimeAccumulated
            undefined,           // endMinute
            Math.round(p.end),   // endTime
            false                // isExtraTime
        )
    );
}

/**
 * Move all goal timestamps to firstHalfStartTime + offset.
 * Each goal gets a unique offset (1ms apart).
 */
export function moveGoalTimestamps(
    matchEvents: IEventMap,
    firstHalfStartTime: number
): IEventMap {
    const newEvents: IEventMap = {};
    let goalOffset = 0;

    for (const [key, event] of Object.entries(matchEvents)) {
        if (event.eventName === EventName.goal) {
            const newTimestamp = firstHalfStartTime + 60000 + goalOffset;
            const movedEvent = { ...event, timestamp: newTimestamp, half: 1, minuteOfPlay: 1 };
            newEvents[String(newTimestamp)] = movedEvent;
            goalOffset += 1; // 1ms offset for unique keys
        } else if (event.eventName !== EventName.half) {
            // Keep non-half, non-goal events as-is
            newEvents[key] = event;
        }
        // Drop existing half events – they'll be replaced
    }

    return newEvents;
}

/** Length of each period the user chose, in whole minutes. */
export interface SavedPeriodLengths {
    warmUp: number;
    period1: number;
    break1: number;
    period2: number;
    warmDown: number;
}

/**
 * Full save flow: creates half events from slider positions,
 * moves goals, updates match timings, and recalculates stats.
 * Returns the chosen period lengths in minutes.
 */
export async function saveMatchTimings(
    match: MatchPhone,
    positions: number[],
    startTime: number,
    endTime: number,
    profile: UserProfile
): Promise<SavedPeriodLengths> {
    const halfEvents = createHalfEventsFromSliders(
        positions,
        startTime,
        endTime
    );

    // Move goal timestamps to 1st half
    const firstHalfStart = halfEvents[1].timestamp; // index 1 = First Half
    let newEvents: IEventMap = moveGoalTimestamps(
        match.matchEvents || {},
        firstHalfStart
    );

    // Add new half events keyed by timestamp
    for (const he of halfEvents) {
        newEvents[String(he.timestamp)] = he;
    }

    // Clone match to avoid mutating Redux state
    const updatedMatch = copyObject(match);
    updatedMatch.matchEvents = newEvents;

    // Set periods and timings (convert ms to minutes)
    const period1Ms = halfEvents[1].length; // First Half duration
    const period2Ms = halfEvents[3].length; // Second Half duration
    const interval1Ms = halfEvents[2].length; // Half Time duration

    updatedMatch.periodsNo = "2";
    updatedMatch.timings = new Timings(period1Ms / 60000, period2Ms / 60000, interval1Ms / 60000);
    updatedMatch.matchFinished = true;

    await recalculateStats(updatedMatch, profile, true);

    // The user has now calibrated playing periods at least once, so the
    // tutorial no longer plays automatically (SCORZA-573).
    updateUserActionsCheckList({ hasCalibratedPlayingPeriods: true });

    const toMinutes = (millis: number) => Math.round(millis / 60000);
    return {
        warmUp: toMinutes(halfEvents[0].length),
        period1: toMinutes(period1Ms),
        break1: toMinutes(interval1Ms),
        period2: toMinutes(period2Ms),
        warmDown: toMinutes(halfEvents[4].length),
    };
}

export async function skipMatchTimings(
    match: MatchPhone,
    profile: UserProfile
): Promise<void> {
    const updatedMatch = copyObject(match);
    updatedMatch.timingsSkipped = true;
    await recalculateStats(updatedMatch, profile, true);
}
